import React from "react";
import { Link } from "react-router-dom";
import { Instagram, Facebook, ArrowUpRight, Shield } from "lucide-react";
import { LEGAL_ENTITY, BRAND } from "../lib/directorate";
import { ALLIANCE_LINKS } from "../lib/allianceLinks";

/**
 * BUSINESS BOOGIE Standard Compliance Footer.
 * Mounted at the App root so it renders on every route — Hub, Trade, Sports, TechOps,
 * auth screens, app shell, commission landing, and the Master Bridge.
 *
 * Public brand displayed: BUSINESS BOOGIE (BRAND.name).
 * Legal operator retained on copyright line: EIGOO Inc. (BRAND.legal_operator).
 */
export default function ComplianceFooter() {
    return (
        <footer
            data-testid="compliance-footer"
            className="relative z-10 mt-16 border-t border-[hsl(40_40%_25%/0.2)] bg-[hsl(151_55%_6%/0.85)] backdrop-blur-sm"
        >
            <div className="max-w-6xl mx-auto px-6 py-8 text-[11px] sm:text-xs leading-relaxed text-muted-foreground">
                {/* Brand line + tagline */}
                <div
                    data-testid="footer-brand"
                    className="mb-5 pb-4 border-b border-[hsl(40_40%_25%/0.18)] flex items-center gap-3 flex-wrap"
                >
                    <span className="font-heading text-xl tracking-tight gold-gradient-text">
                        {BRAND.name_upper}
                    </span>
                    <span className="text-muted-foreground/50">·</span>
                    <span
                        className="text-[hsl(45_85%_85%)] text-sm"
                        style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                    >
                        {BRAND.tagline}
                    </span>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div className="font-mono uppercase tracking-[0.18em] text-[10px] text-[hsl(45_85%_68%)]">
                        Fleet Compliance Notice
                    </div>
                    <nav className="flex items-center gap-5 text-[11px] uppercase tracking-[0.25em]">
                        <Link
                            to="/intel"
                            data-testid="footer-intel-link"
                            className="text-[hsl(45_85%_68%)] hover:text-[hsl(45_85%_85%)] transition-colors"
                        >
                            Fleet Intel
                        </Link>
                        <Link
                            to="/"
                            data-testid="footer-hub-link"
                            className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                        >
                            Main Hub
                        </Link>
                        <Link
                            to="/login"
                            data-testid="footer-login-link"
                            className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors"
                        >
                            Bridge
                        </Link>
                        {/* Sovereign Social Cloak — /ig and /fb route through
                            the platform redirect so the public URL stays
                            on-brand and all clicks are logged for the
                            Director Bridge HUD telemetry pipe. */}
                        <span aria-hidden className="hidden md:inline text-muted-foreground/40">·</span>
                        <Link
                            to="/ig"
                            data-testid="footer-social-instagram"
                            aria-label="Instagram · empowerinvestgrow"
                            className="text-muted-foreground hover:text-[#E8C56C] transition-colors inline-flex items-center"
                        >
                            <Instagram size={15} />
                        </Link>
                        <Link
                            to="/fb"
                            data-testid="footer-social-facebook"
                            aria-label="Facebook · empowerinvestgrow"
                            className="text-muted-foreground hover:text-[#00E5FF] transition-colors inline-flex items-center"
                        >
                            <Facebook size={15} />
                        </Link>
                    </nav>
                </div>
                <p className="max-w-4xl">
                    © 2026 {BRAND.name}{" "}
                    <span className="text-muted-foreground/60">·</span>{" "}
                    operated by {BRAND.legal_operator}{" "}
                    <span className="text-muted-foreground/60">|</span>{" "}
                    <span className="text-foreground/80">
                        Signal Intelligence &amp; Predictive Analytics Only.
                    </span>{" "}
                    <span className="font-medium text-foreground/90">Non-Execution Disclosure:</span> {BRAND.name} does not
                    execute, facilitate, or clear financial trades or wagers.{" "}
                    <span className="text-[hsl(45_85%_68%)]">
                        10% of platform revenue supports the CyberKids Academy Workstation Fund.
                    </span>
                </p>

                {/* Legal entity coordinate — official business address of record */}
                <div
                    data-testid="footer-legal-entity"
                    className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] uppercase tracking-[0.3em] text-muted-foreground/80"
                >
                    <span className="text-[hsl(45_85%_68%)] font-mono">
                        {LEGAL_ENTITY.name}
                    </span>
                    <span className="text-muted-foreground/40">·</span>
                    <span className="font-mono normal-case tracking-normal text-[11px]">
                        {LEGAL_ENTITY.address_oneline}
                    </span>
                </div>

                {/* Fleet Alliance Roster · Strike #23 — sister nodes in the
                    Sovereign ecosystem. Outbound UTM-tagged for cross-fleet
                    attribution. */}
                <div
                    data-testid="footer-alliance"
                    className="mt-6 pt-5 border-t border-[hsl(45_70%_47%/0.2)]"
                >
                    <div
                        className="text-[10px] uppercase tracking-[0.35em] mb-3"
                        style={{
                            color: "#C9A227",
                            fontFamily: "'Montserrat', sans-serif",
                            fontWeight: 800,
                        }}
                    >
                        ◢ Fleet Alliance · Sister Nodes
                    </div>
                    <div className="flex flex-wrap gap-3">
                        {ALLIANCE_LINKS.map((node) => (
                            <a
                                key={node.slug}
                                href={node.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`alliance-link-${node.slug}`}
                                className="group inline-flex items-center gap-2 px-3 py-2 transition-all"
                                style={{
                                    background: "rgba(201,162,39,0.05)",
                                    border: "1px solid #1f1f1f",
                                    color: "#A9BCD2",
                                    fontFamily: "'Montserrat', sans-serif",
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = "#C9A227";
                                    e.currentTarget.style.background = "rgba(201,162,39,0.1)";
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = "#1f1f1f";
                                    e.currentTarget.style.background = "rgba(201,162,39,0.05)";
                                }}
                            >
                                <div>
                                    <div
                                        className="text-[11px] uppercase tracking-[0.2em] font-bold"
                                        style={{ color: "#C9A227" }}
                                    >
                                        {node.name}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground">{node.tagline}</div>
                                </div>
                                <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" style={{ color: "#C9A227" }} />
                            </a>
                        ))}
                    </div>
                </div>

                {/* Director's celestial proclamation — carried across every hull */}
                <div
                    data-testid="footer-proclamation"
                    className="mt-5 pt-4 border-t border-[hsl(40_40%_25%/0.18)] flex items-start gap-3 max-w-4xl"
                >
                    <span className="text-[hsl(45_85%_68%)] font-heading text-2xl leading-none">&ldquo;</span>
                    <div>
                        <p
                            className="text-[hsl(45_85%_85%)] text-sm sm:text-base leading-snug"
                            style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontStyle: "italic" }}
                        >
                            We aren&rsquo;t voyaging in water; we are voyaging through the stars in this infinite cyber space.
                        </p>
                        <div className="mt-1 text-[10px] uppercase tracking-[0.35em] text-[hsl(45_85%_68%)]">
                            — Dontae Kirk, Director
                        </div>
                    </div>
                </div>

                {/* Sovereign brand badge · "EMPOWERED BY EIGOO INC" anchored at the
                    bottom of every page (Strike #23). Replaces the former
                    "Made with platform" footprint with a Sovereign attribution. */}
                <div
                    data-testid="footer-empowered-badge"
                    className="mt-6 pt-4 border-t border-[hsl(45_70%_47%/0.25)] flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.4em]"
                    style={{
                        color: "#C9A227",
                        fontFamily: "'Montserrat', sans-serif",
                        fontWeight: 800,
                    }}
                >
                    <Shield size={12} strokeWidth={2.2} />
                    Empowered by EIGOO INC
                </div>
            </div>
        </footer>
    );
}
